import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/use-toast";

export function BulkEditModal({
  open,
  onOpenChange,
  selectedTasks,
  onSuccess,
  onDelete,
  onBulkEdit,
  categories = [],
  isSubmitting = false,
  canDefer = false,
}) {
  const [actionType, setActionType] = useState(null); // 'update' or 'delete'
  const [formData, setFormData] = useState({
    status: "",
    priority: "",
    category: "",
    due: null,
    deferDate: null,
  });

  const [errors, setErrors] = useState({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setFormData({
        status: "",
        priority: "",
        category: "",
        due: null,
        deferDate: null,
      });
      setErrors({});
      setActionType(null);
    }
  }, [open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // At least one field must be filled
    const hasChanges = Object.values(formData).some(value => 
      value !== "" && value !== null
    );
    
    if (!hasChanges) {
      newErrors.general = "Please select at least one field to update";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setActionType('update');
    const updates = {};
    
    if (formData.status) updates.status = formData.status;
    if (formData.priority) updates.priority = parseInt(formData.priority);
    if (formData.category) updates.category = formData.category;
    if (formData.due) updates.due = format(formData.due, 'yyyy-MM-dd');
    if (formData.deferDate) updates.defer_date = format(formData.deferDate, 'yyyy-MM-dd');

    await onBulkEdit(updates);
    setActionType(null);
  };

  const clearField = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: field.includes('Date') ? null : ""
    }));
  };

  const handleDelete = async () => {
    setActionType('delete');
    await onDelete();
    setActionType(null);
  };

  const statuses = [
    { value: "To Do", label: "To Do" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
  ];

  const priorities = [
    { value: "1", label: "1 (Lowest)" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5", label: "5" },
    { value: "6", label: "6" },
    { value: "7", label: "7" },
    { value: "8", label: "8" },
    { value: "9", label: "9" },
    { value: "10", label: "10 (Highest)" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-none h-screen sm:h-auto sm:max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Edit Tasks</DialogTitle>
          <DialogDescription>
            Update {selectedTasks.length} selected task{selectedTasks.length !== 1 ? 's' : ''}. 
            Leave fields empty to keep current values.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {errors.general && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
              {errors.general}
            </div>
          )}

          {/* Status */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="status" className="text-left">
              Status
            </Label>
            <div className="col-span-3 flex items-center gap-2">
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange('status', value)}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Keep current" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.status && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => clearField('status')}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Priority */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="priority" className="text-left">
              Priority
            </Label>
            <div className="col-span-3 flex items-center gap-2">
              <Select
                value={formData.priority}
                onValueChange={(value) => handleInputChange('priority', value)}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Keep current" />
                </SelectTrigger>
                <SelectContent>
                  {priorities.map((priority) => (
                    <SelectItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.priority && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => clearField('priority')}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Category */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="category" className="text-left">
              Category
            </Label>
            <div className="col-span-3 flex items-center gap-2">
              <Select
                value={formData.category}
                onValueChange={(value) => handleInputChange('category', value)}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Keep current" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.name} value={category.name}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.category && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => clearField('category')}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Defer Date - only show if all selected tasks are deferrable */}
          {canDefer && (
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-left">
                Defer Date
              </Label>
              <div className="col-span-3 flex items-center gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "flex-1 justify-start text-left font-normal",
                        !formData.deferDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.deferDate ? format(formData.deferDate, "PPP") : "Keep current"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.deferDate}
                      onSelect={(date) => handleInputChange('deferDate', date)}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {formData.deferDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => clearField('deferDate')}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="dark:border-gray-700 dark:hover:bg-gray-800"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isSubmitting && actionType === 'delete'}
            className="dark:bg-red-500 dark:text-white dark:hover:bg-red-600"
          >
            {isSubmitting && actionType === 'delete' ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Deleting...
              </div>
            ) : (
              `Delete ${selectedTasks.length} Task${selectedTasks.length !== 1 ? 's' : ''}`
            )}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting && actionType === 'update'}
            className="dark:bg-blue-500 dark:text-white dark:hover:bg-blue-600"
          >
            {isSubmitting && actionType === 'update' ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Updating...
              </div>
            ) : (
              `Update ${selectedTasks.length} Task${selectedTasks.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 